/**
 * ============================================================================
 * ASSISTENT.JS v16.2 - INFO BUTTON L4 MINI
 * ============================================================================
 *
 * ✅ v16.2: INFO BUTTON → L4 Mini
 *           - infoBtn: 44px → 32px + ::before 44px Touch (L4 Mini)
 *           - infoCloseBtn: 36px → 32px + ::before 44px Touch (L4 Mini)
 *
 * ✅ v16.1: BUTTON STRATEGY ALIGNMENT
 *           - headerBtn: padding → height:36px, radius:100px (Chip/Pill)
 *           - infoBtn: 36/40px → 44px (L4 Icon, beide Breakpoints!)
 *           - infoCloseBtn: 32px → 36px + padding für 44px Touch
 *           - attachBtn/cameraBtn/sendBtn: 48px → 44px (L4 Icon)
 *           - input height: 48px → 44px (gleiche Zeile wie Buttons)
 *           - suggestionBtn: padding → height:36px, radius:100px (Chip)
 *           - uploadBtn: padding → height:44px (L2 Secondary)
 *
 * ✅ v16.0: INFO MODAL
 * 🔧 v15.0: TOKEN CONTEXT
 * ✅ v14.1: FIX - nutzt /track/batch statt /track/event
 * ✅ v14.0: Sicheres Analytics mit direktem Tracking
 * ✅ v13.0: Analytics Tracking für eingeloggte User
 * ✅ v12.0: PLANIVIO Rebranding
 * ✅ v11.0: ANTI-FLACKER System
 *
 * @version 16.1.0 - Button Strategy Alignment
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Assistant.css';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext';
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import { Skeleton } from '../common/Loading';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.planivio.de';

const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Assistent').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    
    const response = await fetch(`${API_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else {
      console.warn(`❌ Analytics failed: ${response.status}`, eventName);
    }
  } catch (err) {
    console.warn(`❌ Analytics error: ${eventName}`, err.message);
  }
};

const N8N_BASE = 'https://n8n.delightfulpebble-aec8e2aa.northeurope.azurecontainerapps.io';
const N8N_PROD = `${N8N_BASE}/webhook/planivio-assistant`;
const N8N_IMAGE_UPLOAD = `${N8N_BASE}/webhook-test/1a348d73-be5f-48f1-b292-0b3bf03e1616`;

const STORAGE_MESSAGES = 'planivio_assistant_chat';
const STORAGE_DOCS = 'planivio_assistant_docs';

const FILE_TYPES = {
  'application/pdf': '📄',
  'text/plain': '📃',
  'image/png': '🖼️',
  'image/jpeg': '🖼️',
  'application/json': '📊',
  'text/csv': '📊'
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const LOADING_DELAY_MS = 200;

console.log('🤖 Assistent.js v16.1 loaded - BUTTON STRATEGY ALIGNMENT 🎯');

// ============================================================================
// GLOBALER CACHE
// ============================================================================
let GLOBAL_MESSAGES_CACHE = null;
let GLOBAL_DOCS_CACHE = null;
let GLOBAL_HAS_LOADED = false;

function loadMessages() {
  if (GLOBAL_MESSAGES_CACHE !== null) return GLOBAL_MESSAGES_CACHE;
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_MESSAGES) || '[]');
    GLOBAL_MESSAGES_CACHE = data;
    return data;
  } catch { 
    GLOBAL_MESSAGES_CACHE = [];
    return []; 
  }
}

function saveMessages(msgs) {
  GLOBAL_MESSAGES_CACHE = msgs;
  try { localStorage.setItem(STORAGE_MESSAGES, JSON.stringify(msgs)); } catch {}
}

function loadDocs() {
  if (GLOBAL_DOCS_CACHE !== null) return GLOBAL_DOCS_CACHE;
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_DOCS) || '[]');
    GLOBAL_DOCS_CACHE = data;
    return data;
  } catch { 
    GLOBAL_DOCS_CACHE = [];
    return []; 
  }
}

function saveDocs(docs) {
  GLOBAL_DOCS_CACHE = docs;
  try { localStorage.setItem(STORAGE_DOCS, JSON.stringify(docs)); } catch {}
}

export default function Assistent({ active }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { token, loading: tokenLoading } = useToken();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  // ============================================================================
  // STATE
  // ============================================================================
  const [messages, setMessages] = useState(() => loadMessages());
  const [docs, setDocs] = useState(() => loadDocs());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Refs
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasTrackedViewRef = useRef(false);

  // Styles
  const S = getStyles(isDark, isMobile);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Assistent MOUNTED');
    
    if (!GLOBAL_HAS_LOADED) {
      setTimeout(() => {
        if (isMountedRef.current) {
          setIsInitialLoad(false);
          GLOBAL_HAS_LOADED = true;
        }
      }, 100);
    }
    
    return () => {
      isMountedRef.current = false;
      console.log('🔄 Assistent UNMOUNTING');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (token && !hasTrackedViewRef.current) {
      hasTrackedViewRef.current = true;
      console.log('🔐 Assistent: Using token from TokenContext');
      trackAnalyticsEvent(token, 'component_view', { component: 'Assistent' });
    }
  }, [token]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getUrl = useCallback(() => N8N_PROD, []);

  // ============================================================================
  // LOADING HELPERS
  // ============================================================================
  const startLoading = useCallback(() => {
    setLoading(true);
    loadingTimerRef.current = setTimeout(() => {
      if (isMountedRef.current) setShowLoading(true);
    }, LOADING_DELAY_MS);
  }, []);

  const stopLoading = useCallback(() => {
    setLoading(false);
    setShowLoading(false);
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
  }, []);

  // ============================================================================
  // CHAT
  // ============================================================================
  const sendMessage = useCallback(async () => {
    if (!input.trim() || loading) return;

    const text = input.trim();
    const time = new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    if (token) {
      trackAnalyticsEvent(token, 'assistant_chat', { 
        component: 'Assistent',
        hasDocuments: docs.length > 0 
      });
    }

    const userMsg = { id: Date.now(), role: 'user', text, time };
    setMessages(prev => {
      const updated = [...prev, userMsg];
      saveMessages(updated);
      return updated;
    });
    
    setInput('');
    startLoading();

    try {
      const res = await fetch(getUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          action: 'chat', 
          message: text,
          documents: docs.map(d => d.name)
        })
      });

      const data = await res.json();
      const answer = data.response || data.message || data.text || data.output || t('assistant.noResponse');

      const aiMsg = { 
        id: Date.now() + 1, 
        role: 'assistant', 
        text: answer, 
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      };
      
      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev, aiMsg];
          saveMessages(updated);
          return updated;
        });
      }

    } catch (e) {
      const errorMsg = { 
        id: Date.now() + 1, 
        role: 'assistant', 
        text: '❌ ' + e.message, 
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };
      
      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev, errorMsg];
          saveMessages(updated);
          return updated;
        });
      }
    }

    stopLoading();
  }, [input, loading, getUrl, docs, t, startLoading, stopLoading, token]);

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================
  const addFiles = useCallback((fileList) => {
    const validFiles = Array.from(fileList).filter(f => 
      FILE_TYPES[f.type] && f.size <= MAX_FILE_SIZE
    );
    
    setFiles(prev => [...prev, ...validFiles.map(f => ({
      id: Date.now() + Math.random(),
      file: f,
      name: f.name,
      type: f.type,
      size: f.size
    }))]);
  }, []);

  const removeFile = useCallback((id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const uploadFiles = useCallback(async () => {
    if (files.length === 0 || loading) return;
    startLoading();

    if (token) {
      trackAnalyticsEvent(token, 'assistant_upload', { 
        component: 'Assistent',
        fileCount: files.length 
      });
    }

    try {
      for (const fileObj of files) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(fileObj.file);
        });

        await fetch(N8N_IMAGE_UPLOAD, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({
            action: 'upload',
            document: {
              name: fileObj.name,
              type: fileObj.type,
              size: fileObj.size,
              content: base64
            }
          })
        });

        if (isMountedRef.current) {
          setDocs(prev => {
            const updated = [...prev, { id: fileObj.id, name: fileObj.name, type: fileObj.type }];
            saveDocs(updated);
            return updated;
          });
        }
      }

      const successMsg = {
        id: Date.now(),
        role: 'assistant',
        text: `✅ ${files.length} ${t('assistant.documentsUploaded')}:\n${files.map(f => `• ${f.name}`).join('\n')}`,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      };
      
      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev, successMsg];
          saveMessages(updated);
          return updated;
        });
        setFiles([]);
      }

    } catch (e) {
      const errorMsg = {
        id: Date.now(),
        role: 'assistant',
        text: `❌ ${t('assistant.uploadFailed')}: ` + e.message,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };
      
      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev, errorMsg];
          saveMessages(updated);
          return updated;
        });
      }
    }

    stopLoading();
  }, [files, loading, t, startLoading, stopLoading, token]);

  // ============================================================================
  // KAMERA BILD UPLOAD
  // ============================================================================
  const uploadCameraImage = useCallback(async (file) => {
    if (!file || loading) return;
    startLoading();

    if (token) {
      trackAnalyticsEvent(token, 'assistant_camera_upload', {
        component: 'Assistent',
        fileType: file.type,
        fileSize: file.size
      });
    }

    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      });

      const response = await fetch(N8N_IMAGE_UPLOAD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          action: 'camera_upload',
          image: {
            name: file.name || `camera_${Date.now()}.jpg`,
            type: file.type,
            size: file.size,
            content: base64
          },
          timestamp: new Date().toISOString()
        })
      });

      const data = await response.json();

      const successMsg = {
        id: Date.now(),
        role: 'assistant',
        text: `📷 ${t('assistant.imageUploaded') || 'Bild hochgeladen'}:\n• ${file.name || 'Kamera-Bild'}\n${data.message || ''}`,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      };

      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev, successMsg];
          saveMessages(updated);
          return updated;
        });
      }

    } catch (e) {
      const errorMsg = {
        id: Date.now(),
        role: 'assistant',
        text: `❌ ${t('assistant.uploadFailed') || 'Upload fehlgeschlagen'}: ` + e.message,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      if (isMountedRef.current) {
        setMessages(prev => {
          const updated = [...prev, errorMsg];
          saveMessages(updated);
          return updated;
        });
      }
    }

    stopLoading();
  }, [loading, t, startLoading, stopLoading, token]);

  const handleCameraCapture = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) uploadCameraImage(file);
    e.target.value = '';
  }, [uploadCameraImage]);

  // ============================================================================
  // DRAG & DROP
  // ============================================================================
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ============================================================================
  // RENDER: SKELETON UI
  // ============================================================================
  if (isInitialLoad && (isAuthLoading || tokenLoading)) {
    return (
      <div style={S.container}>
        <div style={S.headerFixed}>
          <Skeleton width={48} height={48} borderRadius={12} />
          <div style={{ flex: 1 }}>
            <Skeleton width={120} height={20} style={{ marginBottom: 6 }} />
            <Skeleton width={80} height={14} />
          </div>
        </div>
        
        <div style={{ ...S.chatAreaScrollable, justifyContent: 'center', alignItems: 'center' }}>
          <Skeleton width={200} height={24} style={{ marginBottom: 12 }} />
          <Skeleton width={150} height={16} />
        </div>
        
        <div style={S.bottomFixed}>
          <div style={S.inputArea}>
            <Skeleton width={44} height={44} borderRadius={12} />
            <Skeleton width="100%" height={44} borderRadius={12} style={{ flex: 1 }} />
            <Skeleton width={44} height={44} borderRadius={12} />
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // AUTH CHECK
  // ============================================================================
  if (!isAuthenticated && !isAuthLoading) {
    return (
      <div style={S.container}>
        <div style={S.authMessage}>
          <span style={{ fontSize: '2rem', marginBottom: '12px' }}>🔐</span>
          <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>{t('assistant.pleaseLogin')}</span>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div 
      style={S.container}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {dragging && (
        <div style={S.dragOverlay}>
          <div style={S.dragContent}>
            <span style={{ fontSize: '3rem' }}>📄</span>
            <span>{t('assistant.dropDocuments')}</span>
          </div>
        </div>
      )}

      {/* ===== HEADER (wie Kalender) ===== */}
      <div className={`ast-header-row1${isDark ? ' dark' : ''}`}>
        <div className="ast-header-left">
          <div className="ast-header-icon">🤖</div>
          <div className="ast-header-title-wrap">
            <h1 className="ast-header-title">{t('assistant.title')}</h1>
            <span className="ast-header-subtitle">
              {loading && !showLoading
                ? <span style={S.syncIndicator}><span style={S.syncDot} /> sync</span>
                : showLoading
                  ? `⏳ ${t('assistant.thinking')}`
                  : docs.length > 0
                    ? `📚 ${docs.length} ${t('assistant.documents')}`
                    : `${messages.length} ${t('assistant.messages')}`
              }
            </span>
          </div>
          <button
            className="ast-info-btn"
            onClick={() => setShowInfo(!showInfo)}
            title="Modul-Info"
          >
            ⓘ
          </button>
        </div>

      </div>

      {/* Info Modal */}
      {showInfo && (
        <div style={S.infoModal}>
          <div style={S.infoModalHeader}>
            <span style={S.infoModalTitle}>KI-Assistent - Info</span>
            <button
              style={S.infoCloseBtn}
              className="assistant-info-close-btn"
              onClick={() => setShowInfo(false)}
            >
              ✕
            </button>
          </div>
          <div style={S.infoModalContent}>
            <p style={S.infoText}>
              Der KI-Assistent ist dein intelligenter Helfer für alle Fragen rund um Haushalt, Rezepte und Organisation.
            </p>
            <ul style={S.infoList}>
              <li>💬 Stelle Fragen zu Rezepten und Kochtipps</li>
              <li>📄 Lade Dokumente hoch für kontextbasierte Antworten</li>
              <li>📷 Nutze die Kamera für Bild-Uploads</li>
              <li>🛒 Erhalte Hilfe bei der Einkaufsplanung</li>
              <li>💡 Bekomme Vorschläge für Haushaltsorganisation</li>
              <li>🤖 KI-gestützte Antworten in Echtzeit</li>
            </ul>
          </div>
        </div>
      )}

      {/* Docs Banner */}
      {docs.length > 0 && (
        <div style={S.docsBanner}>
          <span>📚 {t('assistant.ragDocuments')}: </span>
          {docs.map(d => (
            <span key={d.id} style={S.docTag}>
              {FILE_TYPES[d.type] || '📄'} {d.name}
            </span>
          ))}
        </div>
      )}

      {/* ===== SCROLLABLE CHAT AREA ===== */}
      <div style={{
        ...S.chatAreaScrollable,
        opacity: showLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease'
      }}>
        {/* Empty State */}
        {messages.length === 0 && (
          <div style={S.emptyState}>
            <span style={{ fontSize: '4rem', marginBottom: '1rem' }}>💬</span>
            <span style={S.emptyStateTitle}>
              {t('assistant.welcome')}
            </span>
            <span style={S.emptyStateSubtitle}>
              {t('assistant.welcomeHint')}
            </span>
            
            {/* Quick Suggestions — ✅ v16.1: Chip 36px */}
            <div style={S.suggestions}>
              <button 
                style={S.suggestionBtn}
                onClick={() => setInput(t('assistant.suggestion1'))}
              >
                🍳 {t('assistant.suggestion1')}
              </button>
              <button 
                style={S.suggestionBtn}
                onClick={() => setInput(t('assistant.suggestion2'))}
              >
                🛒 {t('assistant.suggestion2')}
              </button>
              <button 
                style={S.suggestionBtn}
                onClick={() => setInput(t('assistant.suggestion3'))}
              >
                💡 {t('assistant.suggestion3')}
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map(msg => (
          <div key={msg.id} style={msg.role === 'user' ? S.userRow : S.aiRow}>
            <div style={{
              ...S.bubble,
              ...(msg.role === 'user' ? S.userBubble : S.aiBubble),
              ...(msg.isError && S.errorBubble)
            }}>
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
              <div style={S.time}>{msg.time}</div>
            </div>
          </div>
        ))}

        {/* Loading */}
        {showLoading && (
          <div style={S.aiRow}>
            <div style={{ ...S.bubble, ...S.aiBubble, ...S.loadingBubble }}>
              <div style={S.typingDots}>
                <span style={S.dot}></span>
                <span style={{ ...S.dot, animationDelay: '0.2s' }}></span>
                <span style={{ ...S.dot, animationDelay: '0.4s' }}></span>
              </div>
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* ===== FIXED BOTTOM AREA ===== */}
      <div style={S.bottomFixed}>
        {/* Selected Files Preview */}
        {files.length > 0 && (
          <div style={S.filesBox}>
            <div style={S.filesHeader}>
              <span style={S.filesHeaderText}>
                📎 {files.length} {t('assistant.filesSelected')}
              </span>
              {/* ✅ v16.1: Upload Button = L2 Secondary 44px */}
              <button onClick={uploadFiles} disabled={loading} style={S.uploadBtn}>
                📤 {t('assistant.upload')}
              </button>
            </div>
            <div style={S.filesList}>
              {files.map(f => (
                <div key={f.id} style={S.fileItem}>
                  <span>{FILE_TYPES[f.type] || '📄'} {f.name}</span>
                  <button onClick={() => removeFile(f.id)} style={S.removeBtn}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Area — ✅ v16.1: Alle 44px Höhe */}
        <div style={S.inputArea}>
          <input
            type="file"
            ref={fileRef}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            multiple
            accept={Object.keys(FILE_TYPES).join(',')}
            style={{ display: 'none' }}
          />

          <input
            type="file"
            ref={cameraRef}
            onChange={handleCameraCapture}
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
          />

          {/* ✅ v16.1: Kamera = L4 Icon 44px */}
          <button
            onClick={() => cameraRef.current?.click()}
            disabled={loading}
            style={S.cameraBtn}
            title={t('assistant.takePhoto') || 'Foto aufnehmen'}
          >
            📷
          </button>

          {/* ✅ v16.1: Attach = L4 Icon 44px */}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            style={S.attachBtn}
            title={t('assistant.attachDocuments')}
          >
            📎
          </button>

          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder={t('assistant.placeholder')}
            disabled={loading}
            style={S.input}
          />
          
          {/* ✅ v16.1: Send = L4 Icon 44px */}
          <button 
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            style={{
              ...S.sendBtn,
              opacity: input.trim() && !loading ? 1 : 0.5,
              cursor: input.trim() && !loading ? 'pointer' : 'not-allowed'
            }}
          >
            📤
          </button>
        </div>
      </div>

      {/* CSS für Animationen */}
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        /* ✅ v16.2: Touch-Targets für Info Buttons (inline styles können kein ::before) */
        .assistant-info-btn::before,
        .assistant-info-close-btn::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// ✅ v16.1: STYLES - BUTTON STRATEGY ALIGNMENT
// ============================================================================
function getStyles(isDark, isMobile = false) {
  const colors = isDark ? {
    bg: '#1F2937',
    bgSecondary: '#111827',
    bgTertiary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    borderLight: '#374151',
    accent: '#4A90A4',
    accentLight: '#6BA893',
    accentBg: 'rgba(74, 144, 164, 0.15)',
    success: '#10B981',
    successBg: 'rgba(16, 185, 129, 0.15)',
    successBorder: '#059669',
    error: '#EF4444',
    errorBg: 'rgba(239, 68, 68, 0.15)',
    errorBorder: '#DC2626',
    chatBg: '#111827',
    bubbleUser: '#374151',
    bubbleAi: '#1F2937',
    inputBg: '#374151'
  } : {
    bg: '#FFFFFF',
    bgSecondary: '#E8F4F8',
    bgTertiary: '#FAFAFA',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    borderLight: '#D4E8EC',
    accent: '#4A90A4',
    accentLight: '#6BA893',
    accentBg: '#E8F4F8',
    success: '#10B981',
    successBg: '#D1FAE5',
    successBorder: '#A7F3D0',
    error: '#EF4444',
    errorBg: '#FEF2F2',
    errorBorder: '#FECACA',
    chatBg: '#FAFAFA',
    bubbleUser: '#FFFFFF',
    bubbleAi: '#FFFFFF',
    inputBg: '#F0F9FF'
  };

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 180px)',
      minHeight: '400px',
      maxHeight: '800px',
      background: colors.bg,
      borderRadius: '16px',
      border: `2px solid ${colors.border}`,
      overflow: 'hidden',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: 'relative',
      boxShadow: isDark 
        ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
        : '0 4px 20px rgba(74, 144, 164, 0.1)'
    },
    
    authMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.text
    },
    
    dragOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'rgba(74, 144, 164, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100,
      color: 'white'
    },
    dragContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      fontSize: '1.2rem',
      fontWeight: 600
    },

    headerFixed: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '1rem 1.25rem',
      background: colors.bgSecondary,
      borderBottom: `2px solid ${colors.border}`,
      flexShrink: 0
    },
    headerIcon: {
      width: '48px',
      height: '48px',
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)'
    },
    headerTitle: {
      fontWeight: 700,
      color: colors.text,
      fontSize: '1.1rem'
    },
    headerSubtitle: {
      fontSize: '0.75rem',
      color: colors.textMuted,
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    syncIndicator: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '0.75rem',
      color: colors.accent
    },
    syncDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: colors.accent,
      animation: 'pulse 1s infinite'
    },

    // ✅ v16.1: Header Buttons — Chip (36px, Pill)
    headerBtn: {
      height: '36px',
      padding: '0 14px',
      border: `1px solid ${colors.border}`,
      borderRadius: '100px',
      background: colors.bgSecondary,
      color: colors.textSecondary,
      cursor: 'pointer',
      fontSize: '0.82rem',
      fontWeight: 500,
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },

    // ✅ v16.3: Info Button — Kalender-Design (44px Desktop, 32px Mobile)
    infoBtn: {
      width: isMobile ? '32px' : '44px',
      height: isMobile ? '32px' : '44px',
      background: isMobile ? 'transparent' : (isDark ? colors.bgSecondary : '#F8FAFC'),
      border: isMobile ? 'none' : `1px solid ${isDark ? colors.border : '#E2E8F0'}`,
      borderRadius: '12px',
      fontSize: isMobile ? '1rem' : '1.25rem',
      color: isDark ? colors.textMuted : '#475569',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      flexShrink: 0,
      boxSizing: 'border-box',
      position: 'relative',
      padding: 0,
      lineHeight: 1,
      marginLeft: isMobile ? '0.25rem' : '0.5rem'
    },

    // ✅ v16.3: Info Modal — Kalender-Design
    infoModal: {
      background: isDark ? colors.bg : 'white',
      border: `2px solid ${colors.border}`,
      borderRadius: '16px',
      marginBottom: '1rem',
      boxShadow: '0 4px 20px rgba(74, 144, 164, 0.15)',
      overflow: 'hidden',
      flexShrink: 0
    },
    infoModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 1.25rem',
      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
      color: 'white'
    },
    infoModalTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    // ✅ v16.3: Info Close — Kalender-Design (32px, 8px radius)
    infoCloseBtn: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      width: '32px',
      height: '32px',
      fontSize: '1rem',
      color: 'white',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      boxSizing: 'border-box',
      position: 'relative',
      padding: 0,
      lineHeight: 1
    },
    infoModalContent: {
      padding: '1.25rem'
    },
    infoText: {
      margin: '0 0 1rem 0',
      color: isDark ? colors.textSecondary : '#64748B',
      fontSize: '0.9rem',
      lineHeight: 1.6
    },
    infoList: {
      margin: 0,
      padding: '0 0 0 1.25rem',
      color: isDark ? colors.textMuted : '#64748B',
      fontSize: '0.85rem',
      lineHeight: 1.8
    },

    docsBanner: {
      padding: '10px 18px',
      background: colors.successBg,
      borderBottom: `2px solid ${colors.successBorder}`,
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
      color: colors.success,
      flexShrink: 0
    },
    docTag: {
      background: colors.bg,
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      border: `1px solid ${colors.successBorder}`,
      color: colors.text
    },

    chatAreaScrollable: {
      flex: 1,
      overflowY: 'auto',
      padding: '1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      background: colors.chatBg
    },
    
    emptyState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      textAlign: 'center',
      padding: '2rem'
    },
    emptyStateTitle: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: colors.text
    },
    emptyStateSubtitle: {
      fontSize: '0.9rem',
      color: colors.textMuted
    },
    
    suggestions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      justifyContent: 'center',
      marginTop: '20px'
    },
    // ✅ v16.1: Suggestion Buttons — Chip (36px, Pill)
    suggestionBtn: {
      height: '36px',
      padding: '0 14px',
      background: colors.accentBg,
      border: `1px solid ${colors.border}`,
      borderRadius: '100px',
      color: colors.text,
      fontSize: '0.82rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: 1,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box'
    },

    userRow: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    aiRow: {
      display: 'flex',
      justifyContent: 'flex-start'
    },
    bubble: {
      maxWidth: '75%',
      padding: '12px 16px',
      borderRadius: '16px',
      lineHeight: 1.5
    },
    userBubble: {
      background: colors.bubbleUser,
      color: colors.text,
      borderBottomRightRadius: '4px',
      border: `2px solid ${colors.border}`,
      boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
    },
    aiBubble: {
      background: colors.bubbleAi,
      color: colors.text,
      borderBottomLeftRadius: '4px',
      border: `2px solid ${colors.border}`,
      boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
    },
    errorBubble: {
      background: colors.errorBg,
      border: `2px solid ${colors.errorBorder}`,
      color: colors.error
    },
    loadingBubble: {
      padding: '16px 24px'
    },
    time: {
      fontSize: '0.65rem',
      opacity: 0.6,
      marginTop: '6px',
      textAlign: 'right'
    },

    typingDots: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center'
    },
    dot: {
      width: '8px',
      height: '8px',
      background: colors.accent,
      borderRadius: '50%',
      animation: 'bounce 1.4s infinite'
    },

    bottomFixed: {
      flexShrink: 0,
      background: colors.bg,
      borderTop: `2px solid ${colors.border}`
    },

    filesBox: {
      padding: '12px 1.25rem',
      background: colors.bgSecondary,
      borderBottom: `1px solid ${colors.border}`
    },
    filesHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px'
    },
    filesHeaderText: {
      fontWeight: 600,
      color: colors.textSecondary
    },
    // ✅ v16.1: Upload Button — L2 Secondary (44px, 12px radius)
    uploadBtn: {
      height: '44px',
      padding: '0 20px',
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: 600,
      boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },
    filesList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px'
    },
    fileItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 10px',
      background: colors.bg,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: '0.85rem',
      color: colors.textSecondary
    },
    removeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: colors.textMuted,
      fontSize: '0.9rem',
      padding: '2px'
    },

    // ✅ v16.1: Input Area — alle Elemente 44px Höhe
    inputArea: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '1rem 1.25rem',
      background: colors.bg
    },
    // ✅ v16.1: Attach = L4 Icon (44px)
    attachBtn: {
      width: '44px',
      height: '44px',
      minWidth: '44px',
      borderRadius: '12px',
      border: `2px solid ${colors.border}`,
      background: colors.bgSecondary,
      fontSize: '1.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      boxSizing: 'border-box'
    },
    // ✅ v16.1: Camera = L4 Icon (44px)
    cameraBtn: {
      width: '44px',
      height: '44px',
      minWidth: '44px',
      borderRadius: '12px',
      border: `2px solid ${colors.accent}`,
      background: colors.accentBg,
      fontSize: '1.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      boxSizing: 'border-box'
    },
    // ✅ v16.1: Input = 44px (gleiche Höhe wie Buttons in der Zeile)
    input: {
      flex: 1,
      height: '44px',
      padding: '0 18px',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      fontSize: '16px',
      outline: 'none',
      color: colors.text,
      backgroundColor: colors.inputBg,
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    // ✅ v16.1: Send = L4 Icon (44px)
    sendBtn: {
      width: '44px',
      height: '44px',
      minWidth: '44px',
      border: 'none',
      borderRadius: '12px',
      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
      color: 'white',
      fontSize: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.3)',
      boxSizing: 'border-box'
    }
  };
}